// Slice 29 — MSSQL rename emitter.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 29.
//
// Emits dialect-native ALTER syntax for body rewrites + table/view rename.
// The planner (renamePlanner.ts) already routes column rename schema-ALTER
// through `DdlStructuralEdit.applyDdlEdit`; this file owns table/view rename
// + object body rewrites only.
//
// Body rewrites: the introspection feed returns the FULL `CREATE …` text
// (`sys.sql_modules.definition` for view / inline TVF / scalar function /
// procedure / trigger). The emitter substitutes the leading `CREATE` (or
// `CREATE OR ALTER`) with `ALTER`, which preserves every modifier between
// the object name and `AS` (notably `WITH SCHEMABINDING` on views, the
// arg-list + `RETURNS` clause on functions, and the `ON <table> AFTER …`
// event-spec on triggers).
//
// DEFAULT / CHECK constraints have no `CREATE` prefix in the body — only the
// expression — so the emitter wraps them in a DROP + ADD pair using dynamic
// SQL that looks up the parent table (and column for DEFAULTs) from the
// catalog. This mirrors the per-engine fetchers' shape (constraint name
// alone in `objectName`; parent table not carried through).

import type { ObjectKind, QualifiedName } from '../../types';

import type {
  DialectRenameEmitter,
  EmitObjectBodyAlterInput,
} from './types';

const DEFAULT_SCHEMA = 'dbo';

function bracket(name: string): string {
  return `[${name.replace(/]/g, ']]')}]`;
}

function quoteSqlLiteral(text: string): string {
  return text.replace(/'/g, "''");
}

function schemaOf(qn: QualifiedName): string {
  return qn.schema && qn.schema.length > 0 ? qn.schema : DEFAULT_SCHEMA;
}

function bracketedQualified(qn: QualifiedName): string {
  return `${bracket(schemaOf(qn))}.${bracket(qn.name)}`;
}

/** Replace the leading `CREATE` (or `CREATE OR ALTER`) in a CREATE-form
 *  module body with `ALTER`. Whitespace + casing of the rest is preserved
 *  byte-for-byte so any `WITH SCHEMABINDING` / arg-list / return-type /
 *  event-spec the original carried survives intact. */
function rewriteCreateAsAlter(body: string): string {
  return body.replace(/^\s*create\s+(or\s+alter\s+)?/i, 'ALTER ');
}

function emitDefaultConstraintAlter(
  constraint: QualifiedName,
  newExpr: string,
): string {
  const schema = schemaOf(constraint);
  const sLit = quoteSqlLiteral(schema);
  const nLit = quoteSqlLiteral(constraint.name);
  const bracketedName = bracket(constraint.name);
  // The DROP/ADD statements run inside `EXEC ('...')`, so every `'` in the
  // emitted text needs to be `''` for the outer string literal — both the
  // bracketed constraint name (`[a]'b]` style) AND the user's expression.
  const exprLit = quoteSqlLiteral(newExpr);
  const nameLit = quoteSqlLiteral(bracketedName);
  return [
    'DECLARE @t NVARCHAR(258), @c NVARCHAR(128);',
    'SELECT @t = QUOTENAME(s.name) + N\'.\' + QUOTENAME(o.name), @c = c.name',
    '  FROM sys.default_constraints dc',
    '  JOIN sys.objects o ON o.object_id = dc.parent_object_id',
    '  JOIN sys.schemas s ON s.schema_id = o.schema_id',
    '  JOIN sys.columns c ON c.object_id = dc.parent_object_id AND c.column_id = dc.parent_column_id',
    `  WHERE dc.name = '${nLit}' AND s.name = '${sLit}';`,
    `EXEC ('ALTER TABLE ' + @t + ' DROP CONSTRAINT ${nameLit}');`,
    `EXEC ('ALTER TABLE ' + @t + ' ADD CONSTRAINT ${nameLit} DEFAULT (${exprLit}) FOR ' + QUOTENAME(@c));`,
  ].join('\n');
}

function emitCheckConstraintAlter(
  constraint: QualifiedName,
  newExpr: string,
): string {
  const schema = schemaOf(constraint);
  const sLit = quoteSqlLiteral(schema);
  const nLit = quoteSqlLiteral(constraint.name);
  const bracketedName = bracket(constraint.name);
  // Same escape discipline as the default-constraint emitter — the DROP/ADD
  // statements run inside `EXEC ('...')` so every `'` in the embedded text
  // (constraint name + user's expression) needs to be doubled.
  const exprLit = quoteSqlLiteral(newExpr);
  const nameLit = quoteSqlLiteral(bracketedName);
  return [
    'DECLARE @t NVARCHAR(258);',
    'SELECT @t = QUOTENAME(s.name) + N\'.\' + QUOTENAME(o.name)',
    '  FROM sys.check_constraints cc',
    '  JOIN sys.objects o ON o.object_id = cc.parent_object_id',
    '  JOIN sys.schemas s ON s.schema_id = o.schema_id',
    `  WHERE cc.name = '${nLit}' AND s.name = '${sLit}';`,
    `EXEC ('ALTER TABLE ' + @t + ' DROP CONSTRAINT ${nameLit}');`,
    `EXEC ('ALTER TABLE ' + @t + ' ADD CONSTRAINT ${nameLit} CHECK (${exprLit})');`,
  ].join('\n');
}

function isModuleKind(kind: ObjectKind): boolean {
  return (
    kind === 'view' ||
    kind === 'materialized-view' ||
    kind === 'inline-tvf' ||
    kind === 'scalar-function' ||
    kind === 'procedure' ||
    kind === 'trigger'
  );
}

export function emitObjectBodyAlter(input: EmitObjectBodyAlterInput): string {
  const { objectKind, objectName, newBody } = input;
  if (objectKind === 'check-constraint') {
    return emitCheckConstraintAlter(objectName, newBody);
  }
  if (objectKind === 'default-constraint') {
    return emitDefaultConstraintAlter(objectName, newBody);
  }
  if (isModuleKind(objectKind)) {
    return rewriteCreateAsAlter(newBody);
  }
  return rewriteCreateAsAlter(newBody);
}

export type SchemaRenameInput = {
  readonly kind: 'table' | 'view';
  readonly from: QualifiedName;
  readonly to: QualifiedName;
};

export function emitTableOrViewSchemaAlter(input: SchemaRenameInput): string {
  const fromArg = quoteSqlLiteral(bracketedQualified(input.from));
  const toArg = quoteSqlLiteral(input.to.name);
  return `EXEC sp_rename '${fromArg}', '${toArg}'`;
}

export type EmitUndoAlterInput =
  | {
      readonly kind: 'body';
      readonly objectKind: ObjectKind;
      readonly objectName: QualifiedName;
      readonly originalBody: string;
    }
  | {
      readonly kind: 'schema';
      readonly schemaRename: SchemaRenameInput;
    };

export function emitUndoAlter(input: EmitUndoAlterInput): string {
  if (input.kind === 'body') {
    return emitObjectBodyAlter({
      objectKind: input.objectKind,
      objectName: input.objectName,
      newBody: input.originalBody,
    });
  }
  return emitTableOrViewSchemaAlter({
    kind: input.schemaRename.kind,
    from: input.schemaRename.to,
    to: input.schemaRename.from,
  });
}

export const mssqlRenameEmitter: DialectRenameEmitter = {
  emitObjectBodyAlter,
  emitTableOrViewSchemaAlter,
};
